/**
 * Conferences & Shows — event_coordinator tool (Allison Rollins).
 *
 * Scaffold (Phase 2 v1): the daily roll-up view for the Conference & Show
 * Coordinator. Reads the deterministic roll-up from crm-service
 * (GET /api/events/briefing) and renders it the way the TMD tab renders a
 * briefing — surfaced tasks sorted by due date, plus per-person nudge groups
 * with an AI "Draft nudge" action (POST /api/events/actions/nudge-draft).
 *
 * Photondev-only during build-out (config.enableConferences + the backend
 * ENABLE_CONFERENCES gate). Until the Cosmos containers exist the briefing
 * returns 503, which this surfaces as a friendly "not set up yet" state.
 *
 * Deferred to later phases: the full per-show workspace (Overview / Team /
 * Expenses editing), the New Event scaffolder, and sending the nudge (this
 * scaffold drafts + copies only).
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Spinner,
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Textarea,
} from "@fluentui/react-components";
import {
  CalendarLtr24Regular,
  Mail24Regular,
  Copy20Regular,
  ArrowClockwise20Regular,
} from "@fluentui/react-icons";

import useCrmApiClient from "../../hooks/useCrmApiClient";
import ConferencesWorkspace from "./ConferencesWorkspace";

// ---------------------------------------------------------------------------
// Types (mirror events_rollup.py output)
// ---------------------------------------------------------------------------

interface EventSummary {
  id: string;
  name: string;
  status: string;
  show_start?: string | null;
  days_to_show_start?: number | null;
  open_task_count: number;
  surfaced_count: number;
}

interface RollupItem {
  event_id: string;
  event_name: string;
  task_id: string;
  name: string;
  category: string;
  scope: string;
  due_date?: string | null;
  days_remaining?: number | null;
  overdue: boolean;
  assignee_id?: string | null;
  assignee_name: string;
  owner: string;
  nudge_name: string;
  nudge_email: string;
}

interface PersonNudgeGroup {
  person_id?: string | null;
  person_name: string;
  person_email: string;
  event_id: string;
  event_name: string;
  tasks: RollupItem[];
  most_urgent_days?: number | null;
}

interface Rollup {
  coordinator: string;
  today: string;
  generated_at?: string;
  events: EventSummary[];
  surfaced: RollupItem[];
  nudges_by_person: PersonNudgeGroup[];
  owner_nudges: RollupItem[];
  overdue_count: number;
  surfaced_count: number;
  upcoming_count: number;
}

interface NudgeDraft {
  draft_available: boolean;
  reason?: string;
  to_email?: string;
  to_name?: string;
  subject?: string;
  body_plain?: string;
  body_html?: string;
  reasoning?: string;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    height: "100%",
    overflowY: "auto",
    padding: tokens.spacingVerticalL,
    maxWidth: "1000px",
    margin: "0 auto",
    boxSizing: "border-box",
  },
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    gap: tokens.spacingVerticalM,
    textAlign: "center",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  sub: { color: tokens.colorNeutralForeground3, marginBottom: tokens.spacingVerticalL },
  eventBanner: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  eventChip: {
    display: "flex",
    flexDirection: "column",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  kpis: { display: "flex", gap: tokens.spacingHorizontalM, marginBottom: tokens.spacingVerticalL },
  kpi: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    textAlign: "center",
    minWidth: "72px",
  },
  kpiNum: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, display: "block" },
  sectionTitle: {
    fontSize: tokens.fontSizeBase200,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: tokens.colorNeutralForeground3,
    margin: `${tokens.spacingVerticalL} 0 ${tokens.spacingVerticalS}`,
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: tokens.fontSizeBase300 },
  th: {
    textAlign: "left",
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    textTransform: "uppercase",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  td: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: tokens.spacingHorizontalM,
  },
  card: { padding: tokens.spacingVerticalM, display: "flex", flexDirection: "column", gap: tokens.spacingVerticalXS },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: tokens.spacingHorizontalS },
  taskList: { margin: 0, paddingLeft: tokens.spacingHorizontalL, fontSize: tokens.fontSizeBase200 },
  draftBody: { width: "100%", minHeight: "220px", fontFamily: tokens.fontFamilyMonospace },
  mono: { fontFamily: tokens.fontFamilyMonospace, color: tokens.colorNeutralForeground3 },
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function daysBadge(days?: number | null): React.ReactNode {
  if (days === null || days === undefined) {
    return <Badge appearance="tint" color="informative">no date</Badge>;
  }
  if (days < 0) return <Badge appearance="tint" color="danger">{-days}d overdue</Badge>;
  if (days <= 7) return <Badge appearance="tint" color="warning">{days}d left</Badge>;
  return <Badge appearance="tint" color="success">{days}d left</Badge>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Conferences: React.FC = () => {
  const styles = useStyles();
  const crmApi = useCrmApiClient();
  const crmApiRef = React.useRef(crmApi);
  crmApiRef.current = crmApi;

  const [loading, setLoading] = useState(true);
  const [rollup, setRollup] = useState<Rollup | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notReady, setNotReady] = useState(false);

  // Per-show workspace navigation (null = roll-up view).
  const [openEventId, setOpenEventId] = useState<string | null>(null);

  // Nudge draft dialog state
  const [activeGroup, setActiveGroup] = useState<PersonNudgeGroup | null>(null);
  const [draft, setDraft] = useState<NudgeDraft | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [editBody, setEditBody] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotReady(false);
    try {
      const data = await crmApiRef.current.get<Rollup>("/api/events/briefing");
      setRollup(data);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("503")) {
        setNotReady(true);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!crmApiRef.current.isConfigured()) {
      setLoading(false);
      setError("crmServiceUrl is empty. Check config.json.");
      return;
    }
    void load();
  }, [load]);

  const openDraft = useCallback(async (group: PersonNudgeGroup) => {
    setActiveGroup(group);
    setDraft(null);
    setEditBody("");
    setDrafting(true);
    try {
      const d = await crmApiRef.current.post<NudgeDraft>("/api/events/actions/nudge-draft", {
        event_id: group.event_id,
        person_id: group.person_id,
      });
      setDraft(d);
      setEditBody(d.body_plain || "");
    } catch (e) {
      setDraft({ draft_available: false, reason: e instanceof Error ? e.message : String(e) });
    } finally {
      setDrafting(false);
    }
  }, []);

  const closeDraft = useCallback(() => {
    setActiveGroup(null);
    setDraft(null);
    setEditBody("");
  }, []);

  // --- render states -------------------------------------------------------

  // Per-show workspace takes over the whole tool surface when an event is open.
  if (openEventId) {
    return (
      <ConferencesWorkspace
        eventId={openEventId}
        coordinator={rollup?.coordinator}
        onBack={() => { setOpenEventId(null); void load(); }}
      />
    );
  }

  if (loading) {
    return (
      <div className={styles.center}>
        <Spinner size="large" />
        <Text>Loading conferences…</Text>
      </div>
    );
  }

  if (notReady) {
    return (
      <div className={styles.center}>
        <CalendarLtr24Regular style={{ fontSize: "40px", color: tokens.colorNeutralForeground3 }} />
        <Text size={500} weight="semibold">Conferences & Shows isn’t set up yet</Text>
        <Text style={{ maxWidth: "460px", color: tokens.colorNeutralForeground3 }}>
          The events store hasn’t been provisioned on this environment. Once the
          Cosmos containers are created and seeded, your shows and their tasks
          will appear here.
        </Text>
        <Button icon={<ArrowClockwise20Regular />} onClick={() => void load()}>Try again</Button>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.center}>
        <Text size={500} weight="semibold">Couldn’t load conferences</Text>
        <Text className={styles.mono} style={{ maxWidth: "560px" }}>{error}</Text>
        <Button icon={<ArrowClockwise20Regular />} onClick={() => void load()}>Try again</Button>
      </div>
    );
  }

  if (!rollup) return null;

  const hasEvents = rollup.events.length > 0;

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <CalendarLtr24Regular />
        <Text size={600} weight="semibold">Conferences & Shows</Text>
      </div>
      <Text className={styles.sub} block>
        Tackle My Day for {rollup.coordinator || "the coordinator"} — what’s due across your active shows, sorted by date.
      </Text>

      {!hasEvents && (
        <Card style={{ padding: tokens.spacingVerticalL }}>
          <Text>No active shows yet. New conferences will appear here once they’re created.</Text>
        </Card>
      )}

      {hasEvents && (
        <>
          <div className={styles.eventBanner}>
            {rollup.events.map((ev) => (
              <div
                key={ev.id}
                className={styles.eventChip}
                role="button"
                tabIndex={0}
                title="Open show workspace"
                onClick={() => setOpenEventId(ev.id)}
                onKeyDown={(e) => { if (e.key === "Enter") setOpenEventId(ev.id); }}
                style={{ cursor: "pointer" }}
              >
                <Text weight="semibold">{ev.name}</Text>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                  {ev.status}
                  {ev.days_to_show_start !== null && ev.days_to_show_start !== undefined
                    ? ` · ${ev.days_to_show_start} days to show`
                    : ""}
                  {` · ${ev.open_task_count} open`}
                </Text>
              </div>
            ))}
          </div>

          <div className={styles.kpis}>
            <div className={styles.kpi}>
              <span className={styles.kpiNum}>{rollup.surfaced_count}</span>
              <Text size={200}>surfaced</Text>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiNum} style={{ color: tokens.colorPaletteRedForeground1 }}>
                {rollup.overdue_count}
              </span>
              <Text size={200}>overdue</Text>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiNum}>{rollup.upcoming_count}</span>
              <Text size={200}>upcoming</Text>
            </div>
          </div>

          <div className={styles.sectionTitle}>Surfaced tasks</div>
          {rollup.surfaced.length === 0 ? (
            <Text style={{ color: tokens.colorNeutralForeground3 }}>Nothing in a nudge window yet — quiet day.</Text>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Task</th>
                  <th className={styles.th}>Scope</th>
                  <th className={styles.th}>Nudge to</th>
                  <th className={styles.th}>Due</th>
                  <th className={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {rollup.surfaced.map((t) => (
                  <tr key={t.task_id}>
                    <td className={styles.td}>{t.name}</td>
                    <td className={styles.td}>
                      <Badge appearance="outline" color={t.scope === "per_person" ? "brand" : "subtle"}>
                        {t.scope === "per_person" ? "person" : "event"}
                      </Badge>
                    </td>
                    <td className={styles.td}>{t.nudge_name || t.owner || "—"}</td>
                    <td className={`${styles.td} ${styles.mono}`}>{t.due_date}</td>
                    <td className={styles.td}>{daysBadge(t.days_remaining)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className={styles.sectionTitle}>Nudges by person</div>
          {rollup.nudges_by_person.length === 0 ? (
            <Text style={{ color: tokens.colorNeutralForeground3 }}>No per-person nudges due.</Text>
          ) : (
            <div className={styles.cards}>
              {rollup.nudges_by_person.map((g) => (
                <Card key={`${g.event_id}:${g.person_id}`} className={styles.card}>
                  <div className={styles.cardHead}>
                    <Text weight="semibold">{g.person_name}</Text>
                    {daysBadge(g.most_urgent_days)}
                  </div>
                  <Text size={200} className={styles.mono}>{g.person_email || "no email on file"}</Text>
                  <ul className={styles.taskList}>
                    {g.tasks.map((t) => (
                      <li key={t.task_id}>
                        {t.name} · <span className={styles.mono}>{t.due_date}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    appearance="primary"
                    icon={<Mail24Regular />}
                    size="small"
                    disabled={!g.person_email}
                    onClick={() => void openDraft(g)}
                    style={{ marginTop: tokens.spacingVerticalXS }}
                  >
                    Draft nudge
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Nudge draft dialog */}
      <Dialog open={!!activeGroup} onOpenChange={(_, d) => { if (!d.open) closeDraft(); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>
              Nudge {activeGroup?.person_name}
            </DialogTitle>
            <DialogContent>
              {drafting && (
                <div style={{ display: "flex", gap: tokens.spacingHorizontalS, alignItems: "center", padding: tokens.spacingVerticalM }}>
                  <Spinner size="tiny" /> <Text>Drafting…</Text>
                </div>
              )}
              {!drafting && draft && !draft.draft_available && (
                <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {draft.reason || "Couldn’t draft this nudge."}
                </Text>
              )}
              {!drafting && draft && draft.draft_available && (
                <>
                  <Text size={200} className={styles.mono} block style={{ marginBottom: tokens.spacingVerticalXS }}>
                    To: {draft.to_name} &lt;{draft.to_email}&gt;
                  </Text>
                  <Text weight="semibold" block style={{ marginBottom: tokens.spacingVerticalS }}>
                    {draft.subject}
                  </Text>
                  <Textarea
                    className={styles.draftBody}
                    value={editBody}
                    onChange={(_, d) => setEditBody(d.value)}
                    resize="vertical"
                  />
                  {draft.reasoning && (
                    <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: tokens.spacingVerticalXS }} block>
                      Why: {draft.reasoning}
                    </Text>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              {draft?.draft_available && (
                <Button
                  icon={<Copy20Regular />}
                  onClick={() => void navigator.clipboard.writeText(editBody)}
                >
                  Copy
                </Button>
              )}
              <Button appearance="secondary" onClick={closeDraft}>Close</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default Conferences;
